import { formatarMoedaBrasileira } from "../../utils/formatarMoedaBrasileira";
import { useMemo } from "react";
import { CustomTable } from "../../componentes/table/CustomTable";

const formatarDataIso = (iso) =>
    iso ? String(iso).substring(0, 10).split("-").reverse().slice(0, 2).join("/") : "";

const formatPercent = (value) =>
    value != null ? `${Number(value).toFixed(2)}%` : "—";

const MetaBadge = ({ atingiu }) => (
    <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${atingiu
                ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                : "bg-rose-50 text-rose-500 border-rose-200"
            }`}
    >
        {atingiu ? "Atingiu" : "Não atingiu"}
    </span>
);

function TabelaComissao({ data, onRowClick, loading }) {

    const columns = useMemo(() => [
        {
            key: "filialNome",
            label: "Filial",
            sortable: true
        },
        {
            key: "funcionarioCodigo",
            label: "Cód Func",
            center: true,
            sortable: true
        },
        {
            key: "funcionarioNome",
            label: "Funcionário",
            sortable: true,
            // comissão parcial: mostra o cadeado, a função e o período a que ela se refere
            render: (value, row) => row?.parcial ? (
                <span>
                    {value}
                    <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 whitespace-nowrap">
                        <i className="fa fa-lock" />
                        Parcial{row.tipoComissao === "MOTORISTA" ? " · Motorista" : row.tipoComissao === "AUXILIAR_ENTREGA" ? " · Auxiliar" : ""}
                        {row.periodoInicial && row.periodoFinal ? ` · ${formatarDataIso(row.periodoInicial)} a ${formatarDataIso(row.periodoFinal)}` : ""}
                    </span>
                </span>
            ) : value
        },
        {
            key: "funcionarioCargo",
            label: "Cargo",
            sortable: true
        },
        {
            key: "valorTotalReceber",
            label: "Valor a Receber",
            sortable: true,
            center: true,
            format: "currency",
            className: 'text-teal-600 font-semibold bg-muted'
        },
        {
            key: "valorTotalConclusao",
            label: "Conclusão de Rota",
            sortable: true,
            format: "currency",
            center: true,
            className: 'font-semibold bg-muted'

        },
        {
            key: "valorTotalCubagem",
            label: "Cubagem",
            sortable: true,
            format: "currency",
            center: true,
            className: 'font-semibold bg-muted'

        },
        {
            key: "valorTotalRetorno",
            label: "Retorno",
            sortable: true,
            format: "currency",
            center: true,
            className: 'font-semibold bg-muted'
        },
        {
            key: "valorTotalTelemetria",
            label: "Seg. Viária",
            sortable: true,
            format: "currency",
            center: true,
            className: 'font-semibold bg-muted'
        },
        {
            key: "diasAfastados",
            label: "Dias não Trab.",
            sortable: true,
            format: "number",
            center: true,
        },
        {
            key: "descontos",
            label: "Descontos",
            sortable: true,
            center: true,
            render: (value) => (
                <span className={value > 0 ? "text-destructive" : ""}>
                    {value ? 'R$ ' + formatarMoedaBrasileira(value) : '-'}
                </span>
            )
        },
        {
            key: "porcentagemTrabalhado",
            label: "% de Dias Trabalhados",
            sortable: true,
            center: true,
            render: (value) => {
                const percent = value != null ? value * 100 : 100;
                return `${percent.toFixed(2)}%`;
            }
        },

        {
            key: "totalSemDesconto",
            label: "Total S/ Descontos",
            sortable: true,
            format: "currency"
        },
        // Grupo Rotas / Auxiliares / Cubos
        {
            key: "quantidadesEscalasDeRota",
            label: "Total Rotas",
            sortable: true,
            center: true,
            format: "number",
            className: "bg-violet-50",
            render: (value) => value ? value.toLocaleString("pt-BR") : "—"
        },
        {
            key: "rotas1Aux",
            label: "Rotas c/ 1 Auxiliar",
            sortable: true,
            center: true,
            format: "number",
            className: "bg-violet-50",
            render: (value) => value ? value.toLocaleString("pt-BR") : "—"
        },
        {
            key: "rotas2Aux",
            label: "Rotas c/ 2 Auxiliar",
            sortable: true,
            center: true,
            format: "number",
            className: "bg-violet-50",
            render: (value) => value ? value.toLocaleString("pt-BR") : "—"
        },
        {
            key: "cubosEntregue1Auxiliar",
            label: "Cubos c/ 1 Auxiliar",
            sortable: true,
            center: true,
            format: "number",
            className: "bg-violet-50",
        },
        {
            key: "cubosEntregue2Auxiliar",
            label: "Cubos c/ 2 Auxiliar",
            sortable: true,
            center: true,
            format: "number",
            className: "bg-violet-50",
        },
        {
            key: "totalCubosEntregues",
            label: "Total Cubos",
            sortable: true,
            center: true,
            format: "number",
            className: "bg-violet-50 font-semibold",
        },
        {
            key: "totalCubosEntreguesComRetorno",
            label: "Cubos Entregues (Retorno)",
            sortable: true,
            center: true,
            format: "number",
            className: "bg-violet-50 font-semibold",
        },

        // Grupo Retorno
        {
            key: "atingiuMetaRetorno",
            label: "Meta Retorno",
            sortable: true,
            center: true,
            className: "bg-amber-50",
            render: (value) => <MetaBadge atingiu={value} />
        },
        {
            key: "totalCubosRetornados",
            label: "Cubos Retornados",
            sortable: true,
            center: true,
            className: "bg-amber-50",
        },
        {
            key: "percentualRetornoTotal",
            label: "% Retorno",
            sortable: true,
            center: true,
            className: "bg-amber-50",
            render: (value, row) => (
                <span className={row.ultrapassouLimite ? "text-rose-600 font-bold" : ""}>
                    {formatPercent(value)}
                </span>
            )
        },
        {
            key: "faixaPremiacao",
            label: "Faixa Retorno",
            sortable: true,
            center: true,
            className: "bg-amber-50",
        },

        // Grupo Conclusão / Greenmile
        {
            key: "atingiuMetaConclusaoGreenmile",
            label: "Meta Conclusão",
            sortable: true,
            center: true,
            className: "bg-blue-50",
            render: (_, row) => <MetaBadge atingiu={row.atingiuMetaConclusaoGreenmile} />
        },
        {
            key: "percentualConclusao",
            label: "% Conclusão",
            sortable: true,
            center: true,
            className: "bg-blue-50",
            render: (value) => (
                <span className={value != null && value >= 80 ? "text-teal-600 font-semibold" : ""}>
                    {formatPercent(value)}
                </span>
            )
        },
        {
            key: "percentualGreenmile",
            label: "% Adesão Greenmile",
            sortable: true,
            center: true,
            className: "bg-blue-50",
            render: (value) => value != null ? formatPercent(value) : "—"
        },

        // Grupo Segurança Viária / Telemetria
        {
            key: "atingiuMetaTelemetria",
            label: "Meta Seg. Viária",
            sortable: true,
            center: true,
            className: "bg-emerald-50",
            render: (value) => <MetaBadge atingiu={value} />
        },
        {
            key: "mediaSegurancaViaria",
            label: "Média Seg. Viária",
            sortable: true,
            center: true,
            className: "bg-emerald-50",
            render: (value) => formatPercent(value)
        },

    ], []);

    return (
        <div className="">
            <CustomTable
                data={data}
                columns={columns}
                showHeader={false}
                exportable
                exportFilename={"Comissões Motoristas e Auxiliares " + new Date().toISOString()}
                loading={loading}
                maxHeight="calc(100vh - 235px)"
                className="text-[11px]"
                onRowSelect={(row) => onRowClick(row)}
                footer={(rows) => {

                    const totais = rows.reduce(
                        (acc, curr) => {
                            acc.totalReceber += curr.valorTotalReceber ?? 0;
                            acc.totalBruto += curr.totalSemDesconto ?? 0;
                            acc.totalDescontos += curr.descontos ?? 0;
                            acc.totalCubagem += curr.valorTotalCubagem ?? 0;
                            acc.totalRetorno += curr.valorTotalRetorno ?? 0;
                            acc.totalConclusao += curr.valorTotalConclusao ?? 0;
                            acc.totalTelemetria += curr.valorTotalTelemetria ?? 0;
                            return acc;
                        },
                        {
                            totalReceber: 0,
                            totalBruto: 0,
                            totalDescontos: 0,
                            totalCubagem: 0,
                            totalRetorno: 0,
                            totalConclusao: 0,
                            totalTelemetria: 0
                        }
                    );

                    return (

                        <div className="flex flex-wrap justify-end gap-6 p-2 font-semibold">

                            <span>
                                Cubagem: {formatarMoedaBrasileira(totais.totalCubagem)}
                            </span>

                            <span>
                                Conclusão: {formatarMoedaBrasileira(totais.totalConclusao)}
                            </span>

                            <span>
                                Retorno: {formatarMoedaBrasileira(totais.totalRetorno)}
                            </span>

                            <span>
                                Seg. Viária: {formatarMoedaBrasileira(totais.totalTelemetria)}
                            </span>

                            <span>
                                Total Bruto: {formatarMoedaBrasileira(totais.totalBruto)}
                            </span>

                            <span className="text-destructive">
                                Descontos: {formatarMoedaBrasileira(totais.totalDescontos)}
                            </span>

                            <span className="text-teal-600 text-[13px]">
                                Total a Pagar: {formatarMoedaBrasileira(totais.totalReceber)}
                            </span>

                        </div>
                    );
                }}
            />
        </div>
    );
}

export default TabelaComissao;